import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Row, Select, Space, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useUnit } from 'effector-react'
import {
  $allPermissionsOptions,
  $userEditProfile,
  attachExtraPermissionMounted,
  attachGroupMounted,
  attachRevokedPermissionMounted,
  detachExtraPermissionMounted,
  detachGroupMounted,
  detachRevokedPermissionMounted,
  permissionsOptionsMounted,
} from './model'
import { UserPermission } from './types'
import { $userGroups, groupsListMounted } from '../../groups/list/model'
import { Guard } from '../../../../features/session/guard'

const { Text } = Typography

type PickOption = { value: string; label: string }

const AttachRow: React.FC<{
  placeholder: string
  options: PickOption[]
  onAttach: (id: string) => void
  disabled?: boolean
}> = ({ placeholder, options, onAttach, disabled }) => {
  const [selected, setSelected] = useState<string | undefined>(undefined)

  const handleAttach = () => {
    if (!selected) return
    onAttach(selected)
    setSelected(undefined)
  }

  return (
    <Row gutter={8} style={{ marginBottom: 12 }}>
      <Col flex="auto">
        <Select
          style={{ width: '100%' }}
          placeholder={placeholder}
          value={selected}
          onChange={setSelected}
          options={options}
          disabled={disabled}
          showSearch
          optionFilterProp="label"
          allowClear
        />
      </Col>
      <Col>
        <Button
          type="primary"
          disabled={!selected || disabled}
          onClick={handleAttach}
        >
          Attach
        </Button>
      </Col>
    </Row>
  )
}

export const UserMembershipBlock: React.FC = () => {
  const user = useUnit($userEditProfile)
  const allGroups = useUnit($userGroups)
  const allPermissions = useUnit($allPermissionsOptions)

  useEffect(() => {
    groupsListMounted()
    permissionsOptionsMounted()
  }, [])

  const assignedGroupIds = useMemo(
    () => new Set(user.userGroups?.map((g) => g.id)),
    [user.userGroups],
  )
  const extraIds = useMemo(
    () => new Set(user.extraPermissions?.map((p) => p.id)),
    [user.extraPermissions],
  )
  const revokedIds = useMemo(
    () => new Set(user.revokedPermissions?.map((p) => p.id)),
    [user.revokedPermissions],
  )

  const availableGroups: PickOption[] = useMemo(
    () =>
      allGroups
        .filter((g) => !assignedGroupIds.has(g.id))
        .map((g) => ({ value: g.id, label: g.name })),
    [allGroups, assignedGroupIds],
  )

  const availableForExtra: PickOption[] = useMemo(
    () =>
      allPermissions
        .filter((p) => !extraIds.has(p.id))
        .map((p) => ({
          value: p.id,
          label: p.description ? `${p.name} — ${p.description}` : p.name,
        })),
    [allPermissions, extraIds],
  )

  const availableForRevoked: PickOption[] = useMemo(
    () =>
      allPermissions
        .filter((p) => !revokedIds.has(p.id))
        .map((p) => ({
          value: p.id,
          label: p.description ? `${p.name} — ${p.description}` : p.name,
        })),
    [allPermissions, revokedIds],
  )

  const groupColumns: ColumnsType<{ id: string; name?: string }> = [
    { title: 'ID', dataIndex: 'id' },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Actions',
      fixed: 'right',
      width: 160,
      render: (item) => (
        <Guard permission="edit_users">
          <Button
            danger
            size="small"
            onClick={() => {
              if (window.confirm(`Detach group "${item.name ?? item.id}"?`)) {
                detachGroupMounted({ groupId: item.id })
              }
            }}
          >
            Detach
          </Button>
        </Guard>
      ),
    },
  ]

  const permColumns = (
    kind: 'extra' | 'revoked',
  ): ColumnsType<UserPermission> => [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Description', dataIndex: 'description' },
    {
      title: 'Actions',
      fixed: 'right',
      width: 160,
      render: (item: UserPermission) => (
        <Guard permission="edit_users">
          <Button
            danger
            size="small"
            onClick={() => {
              if (!window.confirm(`Detach "${item.name}"?`)) return
              if (kind === 'extra') {
                detachExtraPermissionMounted({ permissionId: item.id })
              } else {
                detachRevokedPermissionMounted({ permissionId: item.id })
              }
            }}
          >
            Detach
          </Button>
        </Guard>
      ),
    },
  ]

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card title="Groups">
        <Guard permission="edit_users">
          <AttachRow
            placeholder="Attach group"
            options={availableGroups}
            onAttach={(groupId) => attachGroupMounted({ groupId })}
          />
        </Guard>
        <Table
          dataSource={user.userGroups || []}
          columns={groupColumns}
          bordered
          rowKey="id"
          pagination={false}
        />
      </Card>

      <Card
        title="Extra permissions"
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Granted directly to this user in addition to role permissions
          </Text>
        }
      >
        <Guard permission="edit_users">
          <AttachRow
            placeholder="Attach extra permission"
            options={availableForExtra}
            onAttach={(permissionId) =>
              attachExtraPermissionMounted({ permissionId })
            }
          />
        </Guard>
        <Table
          dataSource={user.extraPermissions || []}
          columns={permColumns('extra')}
          bordered
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>

      <Card
        title="Revoked permissions"
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Explicitly denied for this user, overriding inherited role permissions
          </Text>
        }
      >
        <Guard permission="edit_users">
          <AttachRow
            placeholder="Revoke permission"
            options={availableForRevoked}
            onAttach={(permissionId) =>
              attachRevokedPermissionMounted({ permissionId })
            }
          />
        </Guard>
        <Table
          dataSource={user.revokedPermissions || []}
          columns={permColumns('revoked')}
          bordered
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </Space>
  )
}
